import React, { useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { Button, Input, Card, CardBody, Badge } from "reactstrap";
import { fetchData, postData } from "../../services/apiServices";
import debounce from "lodash/debounce";

const UsersData = () => {
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Toggle user status API call
  const toggleStatus = async (userId) => {
    try {
      const response = await postData(
        `api/v2/admin/auth/users/${userId}/toggle-status`,
      );
      if (response?.status) {
        fetchUsers(currentPage, search);
      }
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };

  const columns = [
    {
      name: "Username",
      selector: (row) => row.userName || row.username || "-",
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone_number || "-",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => {
        const isActive = row.isActive ?? true; // default to true
        return (
          <Button
            color={isActive ? "success" : "secondary"}
            size="sm"
            onClick={() => toggleStatus(row._id)}
            style={{
              minWidth: "60px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            {isActive ? "ON" : "OFF"}
          </Button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Link to={`/users/${row._id}`}>
            <Button color="info" size="sm">
              <i className="ri-eye-line" />
            </Button>
          </Link>
          <Link to={`/users/update/${row._id}`}>
            <Button color="warning" size="sm">
              <i className="ri-edit-line" />
            </Button>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  const fetchUsers = async (page = 1, searchQuery = "") => {
    setPending(true);
    try {
      const params = { page, limit: perPage, search: searchQuery };
      const response = await fetchData("api/v2/admin/auth/users", params);

      if (response?.status && response?.data?.users) {
        setRows(response.data.users);
        setTotalRows(
          response.data.pagination?.total || response.data.users.length,
        );
      } else {
        setRows([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setRows([]);
      setTotalRows(0);
    } finally {
      setPending(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      fetchUsers(1, value);
      setCurrentPage(1);
    }, 300),
    [perPage],
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page, search);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchUsers(page, search);
  };

  useEffect(() => {
    fetchUsers(1, "");
  }, [perPage]);

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between mb-3">
          <Input
            type="search"
            placeholder="Search users..."
            value={search}
            onChange={handleSearch}
            style={{ width: 250 }}
          />
        </div>

        <DataTable
          columns={columns}
          data={rows}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={pending}
          responsive
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
        />
      </CardBody>
    </Card>
  );
};

export { UsersData };
